import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import styles from '../styles/AboutPage.module.css';
import { Target, Compass } from 'lucide-react'; // Example using Lucide icons
import Seo from '../components/seo';

<Seo 
  title="About Us - The Team Behind Your B2B Sales Pipeline | InviteEngine" 
  description="Learn about the founders and mission behind InviteEngine. We are B2B outreach specialists helping SMBs build authentic sales pipelines."
  path="/about" 
>
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Organization",
        "name": "InviteEngine",
        "url": "https://www.inviteengine.com/",
        "description": "B2B outreach specialists helping UK SMBs build authentic sales pipelines.",
        "knowsAbout": [
          "B2B Lead Generation",
          "Sales Pipeline Management",
          "Email Outreach Strategy"
        ],
        "areaServed": "worldwide"
      }
    })}
  </script>
</Seo>

const TEAM = [
  { name: 'Alex Rivera',   role: 'CEO & Co-Founder',         emoji: '👨‍💼', bio: '10+ years in B2B sales development. Previously scaled SDR teams at 3 Series-B startups.' },
  { name: 'Priya Nair',    role: 'Head of Email Strategy',   emoji: '👩‍💻', bio: 'Former email marketing lead who has written sequences with 40%+ reply rates at scale.' },
  { name: 'Tom Brennan',   role: 'Head of Phone Outreach',   emoji: '👨‍💼', bio: 'Built and managed call teams across EMEA and North America for 7 years.' },
  { name: 'Lena Kovacs',   role: 'Client Success Director',  emoji: '👩‍🎤', bio: 'Ensures every client sees ROI within 30 days. Expert in ICP refinement and pipeline metrics.' },
];

const VALUES = [
  { icon: '🎯', title: 'Results First',       desc: 'We only celebrate wins when our clients do. Every decision is measured against pipeline impact.' },
  { icon: '🤝', title: 'Radical Transparency', desc: 'No black boxes. You see everything — sequences, replies, call recordings, and booking data.' },
  { icon: '✍️', title: 'Human Touch',         desc: 'Real writers. Real callers. No bots pretending to be your SDRs.' },
  { icon: '⚡', title: 'Move Fast',            desc: 'First outreach goes live within 48 hours. We\'re builders, not planners.' },
  { icon: '📈', title: 'Continuous Learning',  desc: 'Every campaign teaches us something. We apply that learning to the next sequence.' },
  { icon: '💎', title: 'Long-Term Thinking',   desc: 'We build pipeline relationships, not one-off blasts. Quality always beats volume.' },
];

export default function AboutPage() {
  useScrollReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className={styles.page}>

      {/* ── PAGE HERO ── */}
      <section className={styles.pageHero}>
        <div className={styles.pageHeroBg} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className={`text-center ${styles.pageHeroContent}`}>
            <span className={styles.breadcrumb}><Link to="/">Home</Link> / About Us</span>
            <h1 className={styles.pageTitle}>The Team Behind Your Pipeline</h1>
            <p className={styles.pageSubtitle}>
              We're a team of outreach specialists who believe every great deal starts with the right conversation
            </p>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}

{/* ── MISSION SECTION ── */}
<section className={styles.missionSection}>
  <div className="container">
    <div className={styles.missionContent}>
      <div className={styles.labelWrapper}>
        <span className={styles.sectionLabel}>WHO WE ARE</span>
      </div>

      <h2 className={styles.sectionTitle}>
        InviteEngine - Where Precision Meets Conversation
      </h2>

      <div className={styles.storyWrapper}>
        <p className={styles.bodyText}>
          As Founders, we’ve spent more than a decade helping businesses break through the noise and connect with the people who actually make decisions.
        </p>

        <p className={styles.bodyText}>
          Over the years, we’ve seen a common pattern. Many SMBs don’t struggle because their products or services aren’t good. They struggle because the right people simply never hear about it. Great solutions, wrong inboxes.
        </p>

        <h3 className={styles.highlightTitle}>
          That’s exactly why we started <span className={styles.brandName}>inviteEngine.</span>
        </h3>

        <p className={styles.bodyText}>
          We wanted to bring strategy, precision, and a genuine human touch back into outreach. No spam blasts. No gimmicks. No wasted effort. Just thoughtful emails and conversations that open doors and create real opportunities.
        </p>

        <p className={styles.bodyText}>
          We care about building pipelines that actually move the needle, outreach that starts meaningful conversations and helps SMBs grow in a way that feels authentic and sustainable.
        </p>

        <p className={styles.finalQuote}>
          Because at the end of the day, growth starts with the <strong>right conversation.</strong>
        </p>
      </div>
    </div>
  </div>
</section>



{/* ── MISSION & VISION ── */}
<section className={styles.dualSection}>
  <div className="container">
    {/* Top Label - Centered */}
    <div className={styles.centeredHeader}>
      <div className={styles.labelWrapper}>
        <span className={styles.sectionLabel}>Our Approach</span>
      </div>
      <h2 className={styles.mainTitle}>
        We understand your business, target the right accounts, run thoughtful outreach, <br />
        <span className={styles.accentText}>and schedule qualified discovery calls, so your team can focus on closing.</span>
      </h2>
    </div>

    {/* The Two-Grid Effect */}
    <div className="row g-4 justify-content-center">
      {/* Mission Column */}
      <div className="col-md-6 col-lg-5">
        <div className={styles.purposeCard}>
          <div className={styles.iconWrapper}>
            <Target size={42} strokeWidth={1.2} />
          </div>
          <h3 className={styles.cardTitle}>Our Mission</h3>
          <p className={styles.cardText}>
            Help SMBs generate meaningful conversations with the right decision-makers through strategic and human-centered outreach.
            We combine data, strategy, and personalized outreach to connect businesses with the people who matter most.
          </p>
        </div>
      </div>

      {/* Vision Column */}
      <div className="col-md-6 col-lg-5">
        <div className={styles.purposeCard}>
          <div className={styles.iconWrapper}>
            <Compass size={42} strokeWidth={1.2} />
          </div>
          <h3 className={styles.cardTitle}>Our Vision</h3>
          <p className={styles.cardText}>
            Create a world where business growth is driven by authentic conversations.
            We believe real growth comes from genuine human connections, not just automated messages or mass outreach.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>


      {/* ── CTA ── */}
      <section className={styles.aboutCta}>
        <div className="container text-center">
          <h2 className={`${styles.sectionTitle} reveal`} style={{ color: '#fff' }}>
            Ready to Meet Your Pipeline Team?
          </h2>
          <p className={`reveal delay-1`} style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', fontSize: '1rem' }}>
            Let's talk about your goals and build a strategy together.
          </p>
          <div className="reveal delay-2">
            <Link to="/contact" className="btn-primary-ie" style={{ marginRight: '1rem' }}>Book a Free Strategy Call</Link>
            <Link to="/services" className="btn-outline-ie">See Our Services</Link>
          </div>
        </div>
      </section>

    </main>
  );
}
